import { usePage } from '@inertiajs/vue3';
import { computed, onUnmounted, ref, watch } from 'vue';

import { apiFetch, useApiMutation, useApiQuery, useQueryClient } from '@ja/portal-query';
import { useRealtimeSubscription } from '@ja/realtime';
import type { RealtimeFrame } from '@ja/realtime';

import type {
    NewNotificationBroadcast,
    NotificationData,
    NotificationReadBroadcast,
    NotificationsResponse,
    ProgressUpdatedBroadcast,
    UnreadCountChangedBroadcast,
    UnreadCountResponse,
} from '../types/notification';

type NotificationsRealtimeEvent =
    | NewNotificationBroadcast
    | NotificationReadBroadcast
    | UnreadCountChangedBroadcast
    | ProgressUpdatedBroadcast;

/**
 * Query keys for notifications.
 */
const QUERY_KEYS = {
    notifications: (params?: NotificationParams) => ['notifications', 'list', params] as const,
    unreadCount: ['notifications', 'unread-count'] as const,
} as const;

interface NotificationParams {
    unread?: boolean;
    category?: string;
    per_page?: number;
}

function buildNotificationsUrl(params?: NotificationParams): string {
    const queryParams = new URLSearchParams();
    if (params?.unread) queryParams.set('unread', '1');
    if (params?.category) queryParams.set('category', params.category);
    if (params?.per_page) queryParams.set('per_page', String(params.per_page));

    const queryString = queryParams.toString();
    return `/api/notifications${queryString ? `?${queryString}` : ''}`;
}

/**
 * Composable for managing notifications with real-time updates.
 * Uses @ja/portal-query for data fetching and caching.
 */
export function useNotifications(defaultParams: NotificationParams = { per_page: 10 }) {
    const page = usePage();
    const queryClient = useQueryClient();

    // Current query params
    const queryParams = ref<NotificationParams>(defaultParams);

    // Get user ID from page props
    const userId = computed(() => {
        const auth = (page.props as Record<string, unknown>).auth as { user?: { id: string } } | undefined;
        return auth?.user?.id;
    });

    // Fetch notifications using useApiQuery
    const {
        data: notificationsData,
        isLoading: isLoadingNotifications,
        error: notificationsError,
        refetch: refetchNotifications,
    } = useApiQuery<NotificationsResponse>(
        computed(() => QUERY_KEYS.notifications(queryParams.value)),
        computed(() => buildNotificationsUrl(queryParams.value)),
        {},
        {
            enabled: computed(() => !!userId.value),
            staleTime: 30_000, // 30 seconds
        }
    );

    // Fetch unread count using useApiQuery
    const {
        data: unreadCountData,
        refetch: refetchUnreadCount,
    } = useApiQuery<UnreadCountResponse>(
        QUERY_KEYS.unreadCount,
        '/api/notifications/unread/count',
        {},
        {
            enabled: computed(() => !!userId.value),
            staleTime: 30_000,
        }
    );

    // Derived state
    const notifications = computed(() => notificationsData.value?.data ?? []);
    const unreadCount = computed(() => unreadCountData.value?.count ?? 0);
    const isLoading = computed(() => isLoadingNotifications.value);
    const error = computed(() => notificationsError.value?.message ?? null);

    // Mark as read mutation
    const markAsReadMutation = useApiMutation<NotificationData, Error, string>({
        mutationFn: (notificationId: string) =>
            apiFetch<NotificationData>(`/api/notifications/${notificationId}/read`, {
                method: 'PATCH',
                body: JSON.stringify({}),
            }),
        onSuccess: (updatedNotification) => {
            // Update the notification in the list cache
            queryClient.setQueryData<NotificationsResponse>(
                QUERY_KEYS.notifications(queryParams.value),
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        data: old.data.map((n) =>
                            n.id === updatedNotification.id
                                ? { ...n, is_read: true, read_at: new Date().toISOString() }
                                : n
                        ),
                    };
                }
            );
            // Update unread count
            queryClient.setQueryData<UnreadCountResponse>(
                QUERY_KEYS.unreadCount,
                (old) => ({ count: Math.max(0, (old?.count ?? 1) - 1) })
            );
        },
    });

    // Mark all as read mutation
    const markAllAsReadMutation = useApiMutation<void, Error, void>({
        mutationFn: () =>
            apiFetch<void>('/api/notifications/mark-all-read', {
                method: 'PATCH',
                body: JSON.stringify({}),
            }),
        onSuccess: () => {
            // Update all notifications in cache to read
            queryClient.setQueryData<NotificationsResponse>(
                QUERY_KEYS.notifications(queryParams.value),
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        data: old.data.map((n) => ({
                            ...n,
                            is_read: true,
                            read_at: n.read_at ?? new Date().toISOString(),
                        })),
                    };
                }
            );
            // Reset unread count
            queryClient.setQueryData<UnreadCountResponse>(
                QUERY_KEYS.unreadCount,
                { count: 0 }
            );
        },
    });

    // Delete notification mutation
    const deleteNotificationMutation = useApiMutation<void, Error, { id: string; wasUnread: boolean }>({
        mutationFn: ({ id }) =>
            apiFetch<void>(`/api/notifications/${id}`, {
                method: 'DELETE',
                body: JSON.stringify({}),
            }),
        onSuccess: (_, { id, wasUnread }) => {
            // Remove from list cache
            queryClient.setQueryData<NotificationsResponse>(
                QUERY_KEYS.notifications(queryParams.value),
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        data: old.data.filter((n) => n.id !== id),
                    };
                }
            );
            // Decrease unread count if the notification was unread
            if (wasUnread) {
                queryClient.setQueryData<UnreadCountResponse>(
                    QUERY_KEYS.unreadCount,
                    (old) => ({ count: Math.max(0, (old?.count ?? 1) - 1) })
                );
            }
        },
    });

    /**
     * Fetch notifications with optional params.
     */
    async function fetchNotifications(params?: NotificationParams) {
        if (params) {
            queryParams.value = params;
        }
        await refetchNotifications();
    }

    /**
     * Fetch unread count.
     */
    async function fetchUnreadCount() {
        await refetchUnreadCount();
    }

    /**
     * Mark a notification as read.
     */
    async function markAsRead(notificationId: string) {
        await markAsReadMutation.mutateAsync(notificationId);
    }

    /**
     * Mark all notifications as read.
     */
    async function markAllAsRead() {
        await markAllAsReadMutation.mutateAsync();
    }

    /**
     * Delete a notification.
     */
    async function deleteNotification(notificationId: string) {
        const notification = notifications.value.find((n) => n.id === notificationId);
        const wasUnread = notification ? !notification.is_read : false;
        await deleteNotificationMutation.mutateAsync({ id: notificationId, wasUnread });
    }

    /**
     * Refresh notifications and unread count.
     */
    function refresh() {
        void refetchNotifications();
        void refetchUnreadCount();
    }

    /**
     * Handle new notification broadcast event.
     */
    function handleNewNotification(event: NewNotificationBroadcast) {
        // Convert broadcast payload to NotificationData
        const notification: NotificationData = {
            id: event.id,
            type: event.type,
            title: event.title,
            message: event.message,
            icon: event.icon,
            action_url: event.actionUrl,
            category: event.category,
            priority: event.priority as NotificationData['priority'],
            is_read: false,
            read_at: null,
            is_progressive: event.isProgressive,
            progress_status: event.progressStatus as NotificationData['progress_status'],
            progress_data: event.progressData,
            created_at: event.createdAt,
        };

        // Add to beginning of list via cache update
        queryClient.setQueryData<NotificationsResponse>(
            QUERY_KEYS.notifications(queryParams.value),
            (old) => {
                if (!old) {
                    return { data: [notification], meta: { current_page: 1, from: 1, last_page: 1, per_page: 10, to: 1, total: 1 } };
                }
                return {
                    ...old,
                    data: [notification, ...old.data],
                };
            }
        );

        // Increment unread count
        queryClient.setQueryData<UnreadCountResponse>(
            QUERY_KEYS.unreadCount,
            (old) => ({ count: (old?.count ?? 0) + 1 })
        );
    }

    /**
     * Handle unread count changed broadcast event.
     */
    function handleUnreadCountChanged(event: UnreadCountChangedBroadcast) {
        queryClient.setQueryData<UnreadCountResponse>(
            QUERY_KEYS.unreadCount,
            { count: event.count }
        );
    }

    /**
     * Handle notification read broadcast — keeps other tabs in sync.
     */
    function handleNotificationRead(event: NotificationReadBroadcast) {
        queryClient.setQueryData<NotificationsResponse>(
            QUERY_KEYS.notifications(queryParams.value),
            (old) => {
                if (!old) return old;
                return {
                    ...old,
                    data: old.data.map((n) =>
                        n.id === event.id
                            ? { ...n, is_read: true, read_at: event.read_at }
                            : n
                    ),
                };
            }
        );
    }

    /**
     * Handle progressive notification progress broadcast.
     */
    function handleProgress(event: ProgressUpdatedBroadcast) {
        queryClient.setQueryData<NotificationsResponse>(
            QUERY_KEYS.notifications(queryParams.value),
            (old) => {
                if (!old) return old;
                return {
                    ...old,
                    data: old.data.map((n) =>
                        n.id === event.id
                            ? {
                                ...n,
                                progress_status: event.progress_status as NotificationData['progress_status'],
                                progress_data: event.progress_data,
                            }
                            : n
                    ),
                };
            }
        );
    }

    // Real-time subscription on `notifications:user#<userId>` — fixed-user
    // channel registered by Core\Notifications\Base\NotificationServiceProvider.
    // Centrifugo enforces owner-only access at the namespace level, so no
    // per-request authorizer runs on subscribe.
    const subscriptionChannel = computed(() =>
        userId.value ? `notifications:user#${userId.value}` : '',
    );

    const { unsubscribe: unsubscribeRealtime } = useRealtimeSubscription<
        NotificationsRealtimeEvent
    >(subscriptionChannel, {
        onMessage(frame: RealtimeFrame<NotificationsRealtimeEvent>) {
            switch (frame.event) {
                case 'notifications.new':
                    handleNewNotification(frame.data as NewNotificationBroadcast);
                    return;
                case 'notifications.unread_count_changed':
                    handleUnreadCountChanged(frame.data as UnreadCountChangedBroadcast);
                    return;
                case 'notifications.read':
                    handleNotificationRead(frame.data as NotificationReadBroadcast);
                    return;
                case 'notifications.progress':
                    handleProgress(frame.data as ProgressUpdatedBroadcast);
            }
        },
    });

    // Watch for user changes (e.g., after login) — useRealtimeSubscription's
    // internal watcher re-attaches when `subscriptionChannel` changes, so we
    // only need to trigger a data refetch here.
    watch(userId, (newUserId, oldUserId) => {
        if (newUserId && newUserId !== oldUserId) {
            refresh();
        }
    });

    onUnmounted(() => {
        unsubscribeRealtime();
    });

    return {
        notifications,
        unreadCount,
        isLoading,
        error,
        fetchNotifications,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        refresh,
    };
}
